import { COUNTRY_ISO, CURRENCY_ISO } from "constants/country";
import { emptyProductAddonExportObject, SERVICE_PRODUCTS_IMPORT_HEADERS, ADDON_STATUS_CODE } from "constants/service";
import Country from "data/Country";
import Currency from "data/Currency";
import { IFormData } from "data/Service"
import readExcelFile from "read-excel-file";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Background } from "styles/themeColors";

export const mapFormToServiceRequest = (service: IFormData, countries: Country[], currencies: Currency[]): object => {
  let servicePrices = [];

  if (service.isForCanada) {

    const countryId = countries?.find(country => country.countryIso === COUNTRY_ISO.CA)?.id;
    const currencyId = currencies?.find(currency => currency.currencyIso === CURRENCY_ISO.CAD)?.id;
    const servicePriceExists = service.servicePrices?.find(sp => sp.country?.countryIso === COUNTRY_ISO.CA);

    const servicePrice = {
      pricePerUnit: service.canadaPrice,
      itemsPerUnit: "1",
      currencyId,
      countryId,
      unitOfMeasurementId: service.unitOfMeasurementCaId
    };

    if (servicePriceExists) {
      servicePrices.push({
        ...servicePrice,
        id: servicePriceExists.id
      });
    } else {
      servicePrices.push(servicePrice);
    }
  }

  if (service.isForUSA) {

    const countryId = countries?.find(country => country.countryIso === COUNTRY_ISO.US)?.id;
    const currencyId = currencies?.find(currency => currency.currencyIso === CURRENCY_ISO.USD)?.id;
    const servicePriceExists = service.servicePrices?.find(sp => sp.country?.countryIso === COUNTRY_ISO.US);

    const servicePrice = {
      pricePerUnit: service.usaPrice,
      itemsPerUnit: "1",
      currencyId,
      countryId,
      unitOfMeasurementId: service.unitOfMeasurementUsaId
    };

    if (servicePriceExists) {
      servicePrices.push({
        ...servicePrice,
        id: servicePriceExists.id
      });
    } else {
      servicePrices.push(servicePrice);
    }
  }



  const payload: any = {
    sku: service.sku,
    name: service.name,
    serviceTypeId: service.serviceTypeId,
    statusId: service.statusId,
    vendorId: service.vendorId,
    version: 1,
    servicePrices,
    products: service.productVariants?.map(variant => { return { productVariantSku: variant.sku, appliedQty: variant.quantity, optional: variant.isOptional } }),
    businessUnitId: service.businessUnitId
  }

  if (service?.id) payload.id = service.id;

  return payload
}



export function getFieldName(key: string): string {
  if (key.split('-')?.length > 1) key = key.split('-')[0];
  switch (key) {
    case 'productName':
      return 'Product name'
    case 'sku':
      return 'Variant SKU number';
    case 'quantity':
      return 'QTY'
    case 'isOptional':
      return 'Optional'
    case 'productStatus':
      return 'Status'
    default:
      return key;
  }
};

export const getColumnMetaData = (key: any) => {
  if (key instanceof Object) {
    return {
      key: key.name,
      label: getFieldName(key.name!),
      keyId: key.id,
    };
  } else {
    return {
      key,
      label: getFieldName(key),
    };
  }
};


export const verifyServiceProductsFileTemplate = async (file: File | null) => {
  if (!file) return false;

  const data = await readExcelFile(file);
  return validateServiceProductsHeaders(data[0]);
};


export const validateServiceProductsHeaders = (headers: any) => {
  const missingHeaders = SERVICE_PRODUCTS_IMPORT_HEADERS.filter(header => !headers.includes(header));
  return missingHeaders.length === 0;
};

export const handleProductAddonsExport = async (exportedData: any, serviceName: string, countries: Country[]) => {
  const columnsData: any = [];

  if (exportedData.variants?.length > 0) {
    exportedData.variants.forEach((variantObject: any) => {
      const productAddon = createProductAddon(variantObject, serviceName);
      columnsData.push(productAddon);
    });
  } else {
    columnsData.push(emptyProductAddonExportObject);
  }

  await generateExcelFile(columnsData, serviceName);
};

const createProductAddon = (variantObject: any, serviceName: string) => ({
  'SKU ID': variantObject.sku,
  'Optional Add-on': variantObject.optional ? 'YES' : 'NO',
  'QTY': variantObject.appliedQty
});

const generateExcelFile = async (columnsData: any, serviceName: string) => {
  if (columnsData.length === 0) {
    console.error("No data available for export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Add-ons");

  // **Fix: Ensure headers are added correctly**
  const headers = Object.keys(columnsData[0]);
  worksheet.columns = headers.map((header) => ({ header, key: header, width: 20 }));

  // **Fix: Add data rows correctly**
  columnsData.forEach((rowData: any) => {
    worksheet.addRow(rowData);
  });

  // **Fix: Apply styles properly**
  worksheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.font = { size: 10 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: rowNumber === 1 ? "ffffff" : "b3ecff" }
      };
    });
  });

  // **Fix: Save file correctly**
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${serviceName || "unnamed-addon-products"}.xlsx`);

  console.log("Export completed successfully!");
};


/**
 * Returns the display state lable of the addon
 * @param addonState Addon State 
 * @returns 
 */
export const getMappedAddonDisplayState = (addonState: string) => {
  const stateMappings: Record<string, string> = {
    [ADDON_STATUS_CODE.DRAFT]: 'Draft',
    [ADDON_STATUS_CODE.PUBLISHED]: 'Published',
    [ADDON_STATUS_CODE.UNPUBLISHED]: 'Unpublished',
  };

  return { displayState: stateMappings[addonState] || 'New' };
};

export const getAddonPillTheme = (addonState: string) => {
  switch (addonState) {
    case ADDON_STATUS_CODE.DRAFT:
      return 'draft';
    case ADDON_STATUS_CODE.PUBLISHED:
      return 'success';
    case ADDON_STATUS_CODE.UNPUBLISHED:
      return 'warning';
    default:
      return Background.navy;
  }
}